/**
 * ReferenceMiniCatalog — generic web-CRUD страница для мини-справочника
 * (категории, цели, шкалы и т.п.). Поддерживает inline-create / inline-edit /
 * DELETE-guard через usage_count.
 *
 * Запись = `code` (latin) + `name_translations` JSONB (мультиязык). DELETE
 * блокируется при usage_count > 0 (FR-5/FR-6) — фронт показывает замок и
 * блокирует кнопку; backend дублирует guard 409.
 *
 * v1 показывает только ru + en столбцами. Дальнейшие языки — в полный
 * editor (можно сделать drawer как у культур), но v1 достаточно inline'а.
 */
import { type ReactNode, useState } from 'react';
import { Check, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';

export interface MiniCatalogItem {
  id: string;
  code: string;
  name_translations: Record<string, string>;
  usage_count: number;
}

export interface MiniCatalogPayload {
  code: string;
  name_translations: Record<string, string>;
}

export interface ReferenceMiniCatalogProps {
  title: string;
  subtitle?: string;
  createLabel: string;
  /** «N в N культурах», используется в badge при usage_count > 0. */
  usageBadgeUnit: (n: number) => string;
  /** Подсказка под empty/delete-blocked. */
  guardMessage?: string;

  items: MiniCatalogItem[];
  isLoading: boolean;

  onCreate: (payload: MiniCatalogPayload) => Promise<void>;
  onUpdate: (id: string, payload: MiniCatalogPayload) => Promise<void>;
  onDelete: (id: string) => Promise<void>;

  /**
   * Доп. действие в строке, слева от «карандаша» — для справочников, у которых
   * кроме code/name есть свои поля (биологическая культура: латынь, ботаника,
   * шкала фенофаз, pH — миграция 114). Inline-редактор такие поля не тянет,
   * поэтому страница подставляет сюда кнопку, открывающую свой drawer.
   */
  rowActionSlot?: (item: MiniCatalogItem) => ReactNode;
  /** Доп. колонка перед «Использование» (заголовок + рендер значения). */
  extraColumn?: { header: string; width?: string; render: (item: MiniCatalogItem) => ReactNode };
}

interface RowDraft {
  code: string;
  ru: string;
  en: string;
}

function emptyDraft(): RowDraft {
  return { code: '', ru: '', en: '' };
}

/**
 * Нормализуем code на лету: lowercase + выкидываем всё кроме [a-z0-9_].
 * Цель — UX: founder вводит `Crupp` и физически не видит заглавной буквы.
 * Если бы оставили только regex-валидацию на submit, save-кнопка молча
 * disable'илась бы без объяснения (AK-253).
 */
export function normalizeCode(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9_]/g, '');
}

function itemToDraft(it: MiniCatalogItem): RowDraft {
  return {
    code: it.code,
    ru: it.name_translations.ru ?? '',
    en: it.name_translations.en ?? '',
  };
}

function draftToPayload(d: RowDraft): MiniCatalogPayload {
  const tr: Record<string, string> = {};
  if (d.ru.trim()) tr.ru = d.ru.trim();
  if (d.en.trim()) tr.en = d.en.trim();
  return { code: d.code.trim(), name_translations: tr };
}

function draftValid(d: RowDraft): boolean {
  return d.ru.trim().length > 0 || d.en.trim().length > 0;
}

export function ReferenceMiniCatalog({
  title,
  subtitle,
  createLabel,
  usageBadgeUnit,
  guardMessage = 'Используется — нельзя удалить',
  items,
  isLoading,
  onCreate,
  onUpdate,
  onDelete,
  rowActionSlot,
  extraColumn,
}: ReferenceMiniCatalogProps) {
  const [creatingDraft, setCreatingDraft] = useState<RowDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<RowDraft | null>(null);
  const [busy, setBusy] = useState(false);

  const startCreate = () => {
    setEditingId(null);
    setCreatingDraft(emptyDraft());
  };
  const startEdit = (it: MiniCatalogItem) => {
    setCreatingDraft(null);
    setEditingId(it.id);
    setEditDraft(itemToDraft(it));
  };

  const cancelCreate = () => setCreatingDraft(null);
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const submitCreate = async () => {
    if (!creatingDraft || !draftValid(creatingDraft)) return;
    setBusy(true);
    try {
      await onCreate(draftToPayload(creatingDraft));
      setCreatingDraft(null);
    } catch (err) {
      // Отказ сервера — на экран, а не в консоль: 409 usage-гейта и 422
      // валидации иначе выглядели как «сохранилось» (AK-574).
      toast.error(describeApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async () => {
    if (!editingId || !editDraft || !draftValid(editDraft)) return;
    setBusy(true);
    try {
      await onUpdate(editingId, draftToPayload(editDraft));
      cancelEdit();
    } catch (err) {
      // Отказ сервера — на экран, а не в консоль: 409 usage-гейта и 422
      // валидации иначе выглядели как «сохранилось» (AK-574).
      toast.error(describeApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (it: MiniCatalogItem) => {
    if (it.usage_count > 0) return;
    if (!window.confirm(`Удалить «${it.name_translations.ru ?? it.code}»?`)) return;
    setBusy(true);
    try {
      await onDelete(it.id);
    } catch (err) {
      // Отказ сервера — на экран, а не в консоль: 409 usage-гейта и 422
      // валидации иначе выглядели как «сохранилось» (AK-574).
      toast.error(describeApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <Button onClick={startCreate} disabled={creatingDraft !== null || busy}>
          <Plus className="h-4 w-4" />
          {createLabel}
        </Button>
      </div>

      <Card className="mt-5">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: '200px' }}>Код</TableHead>
                <TableHead>Название (RU)</TableHead>
                <TableHead>Название (EN)</TableHead>
                {extraColumn && (
                  <TableHead style={extraColumn.width ? { width: extraColumn.width } : undefined}>
                    {extraColumn.header}
                  </TableHead>
                )}
                <TableHead style={{ width: '160px' }}>Использование</TableHead>
                <TableHead style={{ width: '120px' }} className="text-right">
                  Действия
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creatingDraft && (
                <TableRow className="bg-accent-brand/10">
                  <TableCell>
                    <Input
                      value={creatingDraft.code}
                      onChange={(e) =>
                        setCreatingDraft({
                          ...creatingDraft,
                          code: normalizeCode(e.target.value),
                        })
                      }
                      placeholder="code"
                      className="h-8 font-mono text-xs"
                      autoFocus
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      необязательный
                    </p>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creatingDraft.ru}
                      onChange={(e) =>
                        setCreatingDraft({ ...creatingDraft, ru: e.target.value })
                      }
                      placeholder="название RU"
                      className="h-8"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creatingDraft.en}
                      onChange={(e) =>
                        setCreatingDraft({ ...creatingDraft, en: e.target.value })
                      }
                      placeholder="название EN"
                      className="h-8"
                    />
                  </TableCell>
                  {extraColumn && (
                    <TableCell className="text-sm text-muted-foreground">—</TableCell>
                  )}
                  <TableCell className="text-sm text-muted-foreground">—</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={submitCreate}
                        disabled={!draftValid(creatingDraft) || busy}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelCreate}
                        disabled={busy}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {isLoading && items.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      {extraColumn && <TableCell><Skeleton className="h-4 w-16" /></TableCell>}
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                : items.map((it) => {
                    const inUse = it.usage_count > 0;
                    const editing = editingId === it.id && editDraft;
                    if (editing) {
                      return (
                        <TableRow key={it.id} className="bg-accent-brand/10">
                          <TableCell>
                            <Input
                              value={editDraft.code}
                              onChange={(e) =>
                                setEditDraft({
                                  ...editDraft,
                                  code: normalizeCode(e.target.value),
                                })
                              }
                              className="h-8 font-mono text-xs"
                              autoFocus
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.ru}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, ru: e.target.value })
                              }
                              className="h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editDraft.en}
                              onChange={(e) =>
                                setEditDraft({ ...editDraft, en: e.target.value })
                              }
                              className="h-8"
                            />
                          </TableCell>
                          {extraColumn && (
                            <TableCell className="text-sm text-muted-foreground">
                              {extraColumn.render(it)}
                            </TableCell>
                          )}
                          <TableCell className="text-sm tabular-nums text-muted-foreground">
                            {it.usage_count}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                onClick={submitEdit}
                                disabled={!draftValid(editDraft) || busy}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEdit}
                                disabled={busy}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return (
                      <TableRow key={it.id}>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {it.code}
                        </TableCell>
                        <TableCell className="font-medium">
                          {it.name_translations.ru ?? '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {it.name_translations.en ?? '—'}
                        </TableCell>
                        {extraColumn && (
                          <TableCell className="text-sm text-muted-foreground">
                            {extraColumn.render(it)}
                          </TableCell>
                        )}
                        <TableCell>
                          {inUse ? (
                            <Badge variant="secondary">
                              {it.usage_count} {usageBadgeUnit(it.usage_count)}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {rowActionSlot?.(it)}
                            <button
                              type="button"
                              onClick={() => startEdit(it)}
                              disabled={busy || creatingDraft !== null}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
                              aria-label="Редактировать"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(it)}
                              disabled={busy || inUse}
                              title={inUse ? guardMessage : 'Удалить'}
                              aria-label={inUse ? guardMessage : 'Удалить'}
                              className={cn(
                                'inline-flex h-8 w-8 items-center justify-center rounded-md',
                                inUse
                                  ? 'cursor-not-allowed text-muted-foreground/50'
                                  : 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                              )}
                            >
                              {inUse ? (
                                <Lock className="h-4 w-4" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>

        {!isLoading && items.length === 0 && !creatingDraft && (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <div className="text-sm font-medium text-foreground">Записей пока нет</div>
            <Button size="sm" onClick={startCreate} className="mt-2">
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
